/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { FieldUpdate } from './field-update'
import { Field } from './field'
import { FieldGroupUpdate } from './field-group-update'
import { FieldGroup } from './field-group'
import { FieldCreate } from './field-create'
import { FieldRespond } from './field-respond'
import { FieldUpdateWithId } from './field-update-with-id'
import { BatchUpdateWithIdReq } from './batch-update-with-id-req'
import { FieldGroupRes } from './field-group-res'
import { FieldGroupCreate } from './field-group-create'
import { FieldGroupUpdateWithId } from './field-group-update-with-id'
import { QueryFieldsQuery } from './query-fields-query'
import { QueryFieldGroupsQuery } from './query-field-groups-query'

const buildConfig = createConfigBuilder('growp')

export const fieldApi = {
  updateFieldConfig(
    id: number,
    fieldUpdate: FieldUpdate,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'patch',
      '/field/fields/{id}',
      {
        id,
      },
      null,
      fieldUpdate,
      {
        format: 'json',
        isArray: false,
        defaults: {
          groupId: (v: any) => v || 0,
          label: (v: any) => v || '',
          type: (v: any) => v || null,
          description: (v: any) => v || '',
          groupWeight: (v: any) => v || 0,
          weight: (v: any) => v || 0,
          parentId: (v: any) => v || 0,
          defaultValue: (v: any) => v || null,
          rules: (v: any) => v || null,
          isShow: (v: any) => v || false,
          editable: (v: any) => v || false,
          metadata: (v: any) => v || null,
          options: (v: any) => v || [],
          children: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 更新字段
   * @param id
   * @param fieldUpdate
   * @param [options]
   * @return AxiosResponsePromise<Field>
   */
  updateField(
    id: number,
    fieldUpdate: FieldUpdate,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Field> {
    return axios(fieldApi.updateFieldConfig(id, fieldUpdate, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Field(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useUpdateField(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: FieldUpdate }) =>
        fieldApi.updateField(id, payload),
      mutationOptions
    )
  },

  deleteFieldConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'delete', '/field/fields/{id}', {
      id,
    })
  },

  /**
   * @summary 删除字段
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteField(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(fieldApi.deleteFieldConfig(id, options)) as any
  },

  useDeleteField(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => fieldApi.deleteField(id),
      mutationOptions
    )
  },

  updateFieldGroupConfig(
    id: number,
    fieldGroupUpdate: FieldGroupUpdate,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'patch',
      '/field/groups/{id}',
      {
        id,
      },
      null,
      fieldGroupUpdate,
      {
        format: 'json',
        isArray: false,
        defaults: {
          name: (v: any) => v || '',
          weight: (v: any) => v || 0,
          sortable: (v: any) => v || 0,
          editable: (v: any) => v || false,
        },
      }
    )
  },

  /**
   * @summary 更新字段组
   * @param id
   * @param fieldGroupUpdate
   * @param [options]
   * @return AxiosResponsePromise<FieldGroup>
   */
  updateFieldGroup(
    id: number,
    fieldGroupUpdate: FieldGroupUpdate,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<FieldGroup> {
    return axios(
      fieldApi.updateFieldGroupConfig(id, fieldGroupUpdate, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new FieldGroup(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateFieldGroup(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: FieldGroupUpdate }) =>
        fieldApi.updateFieldGroup(id, payload),
      mutationOptions
    )
  },

  deleteFieldGroupConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'delete', '/field/groups/{id}', {
      id,
    })
  },

  /**
   * @summary 删除字段组
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteFieldGroup(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(fieldApi.deleteFieldGroupConfig(id, options)) as any
  },

  useDeleteFieldGroup(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => fieldApi.deleteFieldGroup(id),
      mutationOptions
    )
  },

  createFieldConfig(
    domain: string,
    fieldCreate: FieldCreate,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/field/{domain}/fields',
      {
        domain,
      },
      null,
      fieldCreate,
      {
        format: 'json',
        isArray: false,
        defaults: {
          name: (v: any) => v || '',
          description: (v: any) => v || '',
          groupWeight: (v: any) => v || 0,
          weight: (v: any) => v || 0,
          parentId: (v: any) => v || 0,
          defaultValue: (v: any) => v || null,
          rules: (v: any) => v || null,
          isShow: (v: any) => v || false,
          editable: (v: any) => v || false,
          metadata: (v: any) => v || null,
          archivedWeight: (v: any) => v || 0,
          archivedIsShow: (v: any) => v || false,
          options: (v: any) => v || [],
          children: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 创建字段
   * @param domain
   * @param fieldCreate
   * @param [options]
   * @return AxiosResponsePromise<Field>
   */
  createField(
    domain: string,
    fieldCreate: FieldCreate,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Field> {
    return axios(fieldApi.createFieldConfig(domain, fieldCreate, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Field(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useCreateField(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ domain, payload }: { domain: string; payload: FieldCreate }) =>
        fieldApi.createField(domain, payload),
      mutationOptions
    )
  },

  queryFieldsConfig(
    domain: string,
    queryFieldsQuery?: QueryFieldsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/field/{domain}/fields',
      {
        domain,
      },
      queryFieldsQuery
    )
  },

  /**
   * @summary 获取某领域下字段列表
   * @param domain
   * @param [queryFieldsQuery]
   * @param [queryFieldsQuery.expand]
   * @param [queryFieldsQuery.sort]
   * @param [queryFieldsQuery.groupNameIn] 查询指定分组名内的表头字段，多个逗号分割
   * @param [options]
   * @return AxiosResponsePromise<FieldRespond[]>
   */
  queryFields<TQuery extends QueryFieldsQuery>(
    domain: string,
    queryFieldsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<FieldRespond, TQuery['expand']>[]> {
    return axios(
      fieldApi.queryFieldsConfig(domain, queryFieldsQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new FieldRespond(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryFields<TQuery extends QueryFieldsQuery>(
    {
      domain,
      query,
    }: {
      domain: string
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<FieldRespond, TQuery['expand']>[]> {
    return useQueryApi<Expand<FieldRespond, TQuery['expand']>[]>(
      fieldApi.queryFields,
      'Field.queryFields',
      queryOptions,
      domain,
      query
    )
  },

  batchUpdateFieldConfig(
    domain: string,
    fieldUpdateWithId: FieldUpdateWithId[],
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'patch',
      '/field/{domain}/fields/$batch',
      {
        domain,
      },
      null,
      fieldUpdateWithId,
      {
        format: 'json',
        isArray: true,
        defaults: {
          groupId: (v: any) => v || 0,
          label: (v: any) => v || '',
          type: (v: any) => v || null,
          description: (v: any) => v || '',
          groupWeight: (v: any) => v || 0,
          weight: (v: any) => v || 0,
          parentId: (v: any) => v || 0,
          defaultValue: (v: any) => v || null,
          rules: (v: any) => v || null,
          isShow: (v: any) => v || false,
          editable: (v: any) => v || false,
          metadata: (v: any) => v || null,
          options: (v: any) => v || [],
          children: (v: any) => v || [],
          archivedWeight: (v: any) => v || 0,
          archivedIsShow: (v: any) => v || false,
        },
      }
    )
  },

  /**
   * @summary 批量更新字段
   * @param domain
   * @param fieldUpdateWithId
   * @param [options]
   * @return AxiosResponsePromise<Field[]>
   */
  batchUpdateField(
    domain: string,
    fieldUpdateWithId: FieldUpdateWithId[],
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Field[]> {
    return axios(
      fieldApi.batchUpdateFieldConfig(domain, fieldUpdateWithId, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new Field(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useBatchUpdateField(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ domain, payload }: { domain: string; payload: FieldUpdateWithId[] }) =>
        fieldApi.batchUpdateField(domain, payload),
      mutationOptions
    )
  },

  batchDeleteFieldConfig(
    domain: string,
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'delete',
      '/field/{domain}/fields/$batch',
      {
        domain,
      },
      null,
      batchUpdateWithIdReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 批量删除字段
   * @param domain
   * @param batchUpdateWithIdReq
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  batchDeleteField(
    domain: string,
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      fieldApi.batchDeleteFieldConfig(domain, batchUpdateWithIdReq, options)
    ) as any
  },

  useBatchDeleteField(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        domain,
        payload,
      }: {
        domain: string
        payload: BatchUpdateWithIdReq
      }) => fieldApi.batchDeleteField(domain, payload),
      mutationOptions
    )
  },

  batchSortFieldConfig(
    domain: string,
    fieldUpdateWithId: FieldUpdateWithId[],
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'patch',
      '/field/{domain}/fields/sort/$batch',
      {
        domain,
      },
      null,
      fieldUpdateWithId,
      {
        format: 'json',
        isArray: true,
        defaults: {
          groupId: (v: any) => v || 0,
          label: (v: any) => v || '',
          type: (v: any) => v || null,
          description: (v: any) => v || '',
          groupWeight: (v: any) => v || 0,
          weight: (v: any) => v || 0,
          parentId: (v: any) => v || 0,
          defaultValue: (v: any) => v || null,
          rules: (v: any) => v || null,
          isShow: (v: any) => v || false,
          editable: (v: any) => v || false,
          metadata: (v: any) => v || null,
          options: (v: any) => v || [],
          children: (v: any) => v || [],
          archivedWeight: (v: any) => v || 0,
          archivedIsShow: (v: any) => v || false,
        },
      }
    )
  },

  /**
   * @summary 批量字段排序
   * @param domain
   * @param fieldUpdateWithId
   * @param [options]
   * @return AxiosResponsePromise<Field[]>
   */
  batchSortField(
    domain: string,
    fieldUpdateWithId: FieldUpdateWithId[],
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Field[]> {
    return axios(
      fieldApi.batchSortFieldConfig(domain, fieldUpdateWithId, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new Field(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useBatchSortField(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ domain, payload }: { domain: string; payload: FieldUpdateWithId[] }) =>
        fieldApi.batchSortField(domain, payload),
      mutationOptions
    )
  },

  queryFieldGroupsConfig(
    domain: string,
    queryFieldGroupsQuery?: QueryFieldGroupsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/field/{domain}/groups',
      {
        domain,
      },
      queryFieldGroupsQuery
    )
  },

  /**
   * @summary 获取某领域下字段分组列表
   * @param domain
   * @param [queryFieldGroupsQuery]
   * @param [queryFieldGroupsQuery.expand]
   * @param [queryFieldGroupsQuery.sort]
   * @param [options]
   * @return AxiosResponsePromise<FieldGroupRes[]>
   */
  queryFieldGroups<TQuery extends QueryFieldGroupsQuery>(
    domain: string,
    queryFieldGroupsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<FieldGroupRes, TQuery['expand']>[]> {
    return axios(
      fieldApi.queryFieldGroupsConfig(domain, queryFieldGroupsQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new FieldGroupRes(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryFieldGroups<TQuery extends QueryFieldGroupsQuery>(
    {
      domain,
      query,
    }: {
      domain: string
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<FieldGroupRes, TQuery['expand']>[]> {
    return useQueryApi<Expand<FieldGroupRes, TQuery['expand']>[]>(
      fieldApi.queryFieldGroups,
      'Field.queryFieldGroups',
      queryOptions,
      domain,
      query
    )
  },

  createFieldGroupConfig(
    domain: string,
    fieldGroupCreate: FieldGroupCreate,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/field/{domain}/groups',
      {
        domain,
      },
      null,
      fieldGroupCreate,
      {
        format: 'json',
        isArray: false,
        defaults: {
          weight: (v: any) => v || 0,
          sortable: (v: any) => v || false,
          editable: (v: any) => v || false,
        },
      }
    )
  },

  /**
   * @summary 创建字段组
   * @param domain
   * @param fieldGroupCreate
   * @param [options]
   * @return AxiosResponsePromise<FieldGroupRes>
   */
  createFieldGroup(
    domain: string,
    fieldGroupCreate: FieldGroupCreate,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<FieldGroupRes> {
    return axios(
      fieldApi.createFieldGroupConfig(domain, fieldGroupCreate, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new FieldGroupRes(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useCreateFieldGroup(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ domain, payload }: { domain: string; payload: FieldGroupCreate }) =>
        fieldApi.createFieldGroup(domain, payload),
      mutationOptions
    )
  },

  batchUpdateFieldGroupConfig(
    domain: string,
    fieldGroupUpdateWithId: FieldGroupUpdateWithId[],
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'patch',
      '/field/{domain}/groups/$batch',
      {
        domain,
      },
      null,
      fieldGroupUpdateWithId,
      {
        format: 'json',
        isArray: true,
        defaults: {
          name: (v: any) => v || '',
          weight: (v: any) => v || 0,
          sortable: (v: any) => v || 0,
          editable: (v: any) => v || false,
        },
      }
    )
  },

  /**
   * @summary 批量更新字段组
   * @param domain
   * @param fieldGroupUpdateWithId
   * @param [options]
   * @return AxiosResponsePromise<FieldGroup[]>
   */
  batchUpdateFieldGroup(
    domain: string,
    fieldGroupUpdateWithId: FieldGroupUpdateWithId[],
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<FieldGroup[]> {
    return axios(
      fieldApi.batchUpdateFieldGroupConfig(
        domain,
        fieldGroupUpdateWithId,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new FieldGroup(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useBatchUpdateFieldGroup(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        domain,
        payload,
      }: {
        domain: string
        payload: FieldGroupUpdateWithId[]
      }) => fieldApi.batchUpdateFieldGroup(domain, payload),
      mutationOptions
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(fieldApi).forEach((api) => {
  // @ts-ignore
  fieldApi[api]._name_ = `Field.${api}`
})
